import { Box, Text } from '@chakra-ui/react';
import { PatchDataWrapper } from '@diamond/shared/data-access';
import {
  BackofficeActivitiesOrderItem,
  BackofficeActivitiesOrderItemWithVoucher,
  BackofficeOrderUpdate,
} from '@diamond/shared/types';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { NumericFormat } from 'react-number-format';

import { ORDER_CHANGELOG_QUERY_KEY } from '../../../api';
import {
  useUpdateActivitiesDetailColumn,
  useUpdateActivitiesDetailQueryData,
} from '../../../hooks';
import { EditableColumnProps } from '../../../types/activity-column-types';
import {
  formatCurrency,
  FulfillmentStage,
  getActivityItemDisplay,
  getOrderItemDisplayLatestPrice,
  resolveFulfillmentStageUnitPrice,
} from '../../../utils/activity-formatters';
import { ActivityDetailDiffLabel } from '../../activity-detail/activity-detail-stage-qty-cell';

const API_URL = 'backoffice/order';

interface Props
  extends EditableColumnProps<
    BackofficeActivitiesOrderItem,
    number | undefined
  > {
  queryKey?: QueryKey;
  activitiesId: string;
  token?: string;
  refetch: () => void;
  isVoucherEnabled?: boolean;
  stage?: FulfillmentStage;
}

export function LatestPriceColumn({
  info,
  selectedData,
  onUpdate,
  queryKey = [],
  activitiesId,
  token,
  refetch,
  isVoucherEnabled = false,
  stage,
}: Props) {
  const queryClient = useQueryClient();
  const item = info.row.original as BackofficeActivitiesOrderItemWithVoucher;
  const displayValue =
    stage != null
      ? resolveFulfillmentStageUnitPrice(item, stage, isVoucherEnabled)
      : getOrderItemDisplayLatestPrice(item, isVoucherEnabled);
  const display = getActivityItemDisplay(item);

  const { patchData } = PatchDataWrapper<BackofficeOrderUpdate>(
    API_URL,
    token as string,
    'admin'
  );

  const isSelected = selectedData.some(
    (row) => row.original.id === info.row.original.id
  );
  const canOverwritePrice = info.row.original.can_overwrite_price;

  const setQueryData = useUpdateActivitiesDetailQueryData({
    queryKey,
    info,
    name: 'latest_price',
  });

  const setValue = useUpdateActivitiesDetailColumn({
    onUpdate(value) {
      setQueryData(value);
      patchData(
        { latest_price: value },
        undefined,
        `${activitiesId}/item/${info.row.original.id}`
      ).then(async () => {
        refetch();
        await queryClient.invalidateQueries({
          queryKey: [ORDER_CHANGELOG_QUERY_KEY],
        });
      });
    },
  });

  if (!display.showPriceSection) {
    return null;
  }

  if (isSelected && canOverwritePrice) {
    const handlePriceUpdate = (values: { floatValue: number | undefined }) => {
      setValue(String(values.floatValue ?? 0));
    };

    return (
      <Box w="full" display="flex" flexDirection="column" alignItems="center">
        <NumericFormat
          key={`price-${info.row.original.id}-${displayValue}`}
          prefix="Rp"
          thousandSeparator="."
          decimalSeparator=","
          style={{
            borderColor: 'gray',
            borderWidth: 1,
            borderRadius: 4,
            padding: 4,
          }}
          allowNegative={false}
          defaultValue={displayValue || 0}
          onValueChange={handlePriceUpdate}
        />
        <Text
          className="text-xs"
          fontSize={12}
          mt="4px"
          color="red.500"
          textAlign="center"
        >
          *Input harga final
        </Text>
      </Box>
    );
  }

  return (
    <Box w="full" display="flex" flexDirection="column" alignItems="center">
      <Text
        fontSize="sm"
        fontWeight="400"
        color="#373F41"
        textAlign="center"
        w="full"
      >
        {formatCurrency(displayValue || 0)}
      </Text>
      {display.showInitialUnitPrice ? (
        <ActivityDetailDiffLabel
          label="harga awal"
          value={formatCurrency(display.initialUnitPrice)}
          align="center"
        />
      ) : null}
    </Box>
  );
}
